import { useMutation } from "@tanstack/react-query"
import { Check, RotateCw, ScanText, X } from "lucide-react"
import { useEffect, useState } from "react"

import { api, ErrorApi } from "@/api/cliente"
import { type LecturaIngreso as Lectura, NOMBRE_TIPO } from "@/componentes/ingresos/tipos"
import { ProgresoLectura } from "@/componentes/ordenes/ProgresoLectura"
import { monto as comoCifra } from "@/componentes/tesoreria/dinero"
import { Boton } from "@/componentes/ui/Boton"
import { comoFecha } from "@/lib/fechas"

/**
 * Lee con IA el comprobante de un INGRESO y propone sus datos al formulario.
 *
 * Es el lector de los pagos mirado al revés: la cuenta propia es la de DESTINO —a la
 * que llegó el dinero— y el tercero es quien lo ENVÍA. Por eso propone, además del
 * monto, la fecha y la referencia, **a qué cuenta entró y quién lo pagó**, que el
 * servidor decide contra el catálogo y sólo si la coincidencia es única.
 *
 * ⚠ **Se lee SÓLO si se pide**, y nada entra al formulario sin «Usar estos datos»: las
 * mismas dos reglas que el lector de los pagos, y por los mismos motivos —la cuota del
 * nivel gratuito y que un monto mal leído no entre solo—.
 *
 * ⚠ **Sale a Google: sólo con `factura_ia:usar`.** Quien no lo tiene ni ve el botón.
 */
export function LecturaIngreso({
  archivo,
  onUsar,
}: {
  archivo: File
  onUsar: (lectura: Lectura) => void
}) {
  const [cerrada, setCerrada] = useState(false)
  const leer = useMutation({
    mutationFn: (a: File) => {
      const datos = new FormData()
      datos.append("archivo", a)
      return api.post<Lectura>("/api/v1/ingresos/leer-comprobante", datos)
    },
  })
  const { mutate, reset } = leer

  // biome-ignore lint/correctness/useExhaustiveDependencies: se reinicia AL CAMBIAR de archivo
  useEffect(() => {
    setCerrada(false)
    reset()
  }, [archivo, reset])

  if (cerrada) return null
  if (leer.isIdle) {
    return (
      <div className="flex flex-wrap items-center justify-end gap-2 text-xs">
        <span style={{ color: "var(--fg-muted)" }}>
          Opcional: la imagen se envía a Google (Gemini) para leerla.
        </span>
        <Boton tamano="sm" onClick={() => mutate(archivo)}>
          <ScanText size={14} /> Leer con IA
        </Boton>
      </div>
    )
  }
  if (leer.isPending) return <ProgresoLectura archivos={1} documento="comprobante" />
  if (leer.isError) {
    return (
      <div className="space-y-2 text-sm" role="alert">
        <p style={{ color: "var(--destructive)" }}>
          No se pudo leer el comprobante:{" "}
          {leer.error instanceof ErrorApi ? leer.error.message : "error inesperado"}. Reintenta o
          teclea los datos a mano.
        </p>
        <div className="flex justify-end gap-2">
          <Boton tamano="sm" onClick={() => setCerrada(true)}>
            <X size={14} /> Ignorar
          </Boton>
          <Boton tamano="sm" variante="primario" onClick={() => mutate(archivo)}>
            <RotateCw size={14} /> Reintentar
          </Boton>
        </div>
      </div>
    )
  }
  const r = leer.data
  if (!r?.comprobante) return null
  const c = r.comprobante
  const filas: [string, string | null][] = [
    ["Monto", c.monto ? `${comoCifra(c.monto)} ${c.moneda ?? ""}` : null],
    ["Fecha", c.fecha ? comoFecha(c.fecha) : null],
    ["Referencia", c.referencia],
    ["Envía", [c.ordenante, c.ordenante_id].filter(Boolean).join(" · ") || null],
    ["Recibe", [c.beneficiario, c.beneficiario_id].filter(Boolean).join(" · ") || null],
    ["Bancos", [c.banco_origen, c.banco_destino].filter(Boolean).join(" → ") || null],
    [
      "Cuenta donde entró",
      r.cuenta_sugerida ? `${r.cuenta_sugerida.nombre} · ${r.cuenta_sugerida.motivo}` : null,
    ],
    [
      "Pagador",
      r.pagador_sugerido
        ? `${NOMBRE_TIPO[r.pagador_sugerido.tipo]}: ${r.pagador_sugerido.nombre} · ${r.pagador_sugerido.motivo}`
        : null,
    ],
  ]

  return (
    <section className="tarjeta space-y-3 p-3 text-sm" aria-label="Datos leídos del comprobante">
      <h3 className="flex items-center gap-2 font-semibold">
        <ScanText size={16} aria-hidden={true} style={{ color: "var(--primary)" }} />
        Leído del comprobante
        <span className="text-xs font-normal" style={{ color: "var(--fg-muted)" }}>
          · confianza {c.confianza.toLowerCase()}
        </span>
      </h3>
      <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1">
        {filas.map(([etiqueta, valor]) => (
          <div key={etiqueta} className="contents">
            <dt style={{ color: "var(--fg-muted)" }}>{etiqueta}</dt>
            <dd className="min-w-0 break-words tabular-nums">{valor ?? "—"}</dd>
          </div>
        ))}
      </dl>
      {r.bloqueos.length > 0 && (
        <ul className="list-disc space-y-1 pl-5" style={{ color: "var(--destructive)" }}>
          {r.bloqueos.map((b) => (
            <li key={b}>{b}</li>
          ))}
        </ul>
      )}
      {(r.avisos.length > 0 || c.dudas.length > 0) && (
        <ul className="list-disc space-y-1 pl-5" style={{ color: "var(--warning)" }}>
          {r.avisos.map((a) => (
            <li key={a}>{a}</li>
          ))}
          {c.dudas.map((d) => (
            <li key={d}>{d}</li>
          ))}
        </ul>
      )}
      <div className="flex flex-wrap justify-end gap-2">
        <Boton tamano="sm" onClick={() => setCerrada(true)}>
          <X size={14} /> Ignorar
        </Boton>
        {r.se_puede_usar && (
          <Boton
            tamano="sm"
            variante="primario"
            onClick={() => {
              onUsar(r)
              setCerrada(true)
            }}
          >
            <Check size={14} /> Usar estos datos
          </Boton>
        )}
      </div>
    </section>
  )
}
